import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const CartContext = createContext(null);

const EMPTY = {
  cart_id: "",
  lines: [],
  totals: { subtotal: 0, shipping: 0, tax: 0, total: 0, currency: "USD" },
  item_count: 0,
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch (_) {
      setCart(EMPTY);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addToCart = async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/cart/add", payload);
      setCart(data);
      // Trigger VIP popup after a successful add-to-cart (fires once — VipPopup
      // itself checks localStorage to avoid re-showing after dismiss).
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("boscage:vip-trigger"));
      }
      return data;
    } finally {
      setLoading(false);
    }
  };

  const updateLine = async (line_id, quantity) => {
    const { data } = await api.post("/cart/update", { line_id, quantity });
    setCart(data);
  };

  const removeLine = async (line_id) => {
    const { data } = await api.post(
      `/cart/remove?line_id=${encodeURIComponent(line_id)}`,
    );
    setCart(data);
  };

  const checkout = async (shipping) => {
    const { data } = await api.post("/cart/checkout", shipping);
    setCart(EMPTY);
    return data;
  };

  return (
    <CartContext.Provider
      value={{ cart, loading, refresh, addToCart, updateLine, removeLine, checkout }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
